// This module provides access to the global mapboxgl object loaded via CDN
// It ensures type safety while working with the inline script approach

import type * as MapboxGL from 'mapbox-gl'

declare global {
  interface Window {
    mapboxgl: typeof MapboxGL
  }
}

// Wait for mapboxgl to be available since it's loaded asynchronously
export const waitForMapbox = (): Promise<typeof MapboxGL> => {
  return new Promise((resolve) => {
    if (window.mapboxgl) {
      resolve(window.mapboxgl)
      return
    }

    const checkMapbox = (attempts: number = 0) => {
      if (attempts > 25) {
        throw new Error('Mapbox GL JS not yet loaded. Use waitForMapbox() for async access.')
      }

      if (window.mapboxgl) {
        resolve(window.mapboxgl)
      } else {
        const timeout = setTimeout(() => {
          clearTimeout(timeout)
          checkMapbox(attempts + 1)
        }, 50)
      }
    }

    checkMapbox()
  })
}

// Synchronous access (will throw if mapboxgl isn't loaded yet)
const getMapboxSync = () => {
  if (!window.mapboxgl) {
    throw new Error(
      'Mapbox GL JS not yet loaded. Use waitForMapbox() for async access.',
    )
  }
  return window.mapboxgl
}

// Export a lazy promise that resolves to mapboxgl
let _mapboxglPromise: Promise<typeof MapboxGL> | null = null
export const mapboxglPromise = () => {
  if (!_mapboxglPromise) {
    _mapboxglPromise = waitForMapbox()
  }
  return _mapboxglPromise
}

// Lazy getters that wait for mapbox to be available
export const getMapbox = () => getMapboxSync()
export const getMap = () => getMapboxSync().Map
export const getMarker = () => getMapboxSync().Marker
export const getNavigationControl = () => getMapboxSync().NavigationControl
export const getGeoJSONSource = () => getMapboxSync().GeoJSONSource

export type { GeoJSONSource as GeoJSONSourceType } from 'mapbox-gl'

export default getMapboxSync
